<template>
  <div v-if="hasInputs" class="price-inputs-container">
    <UiInput
      class="price-input"
      name="min-price-input"
      :placeholder="minInputPlaceholder"
      :type="minInputType"
      :value="resolveInputVals[0]"
      @input="
        $event => {
          savedInputVals.min = $event
          wrapDebounce(() => {
            setInputVal($event, 'minRange')
          })
        }
      "
      @keyup.enter="
        ($event: any) => {
          wrapDebounce.cancel()
          setInputVal($event.target.value, 'minRange')
        }
      "
      @input-focus="onInputFocus('min')"
      @input-blur="
        () => {
          wrapDebounce.cancel()
          setInputVal(savedInputVals.min, 'minRange')
          onInputBlur('min')
        }
      "
    />
    <UiInput
      class="price-input"
      name="max-price-input"
      :placeholder="maxInputPlaceholder"
      :type="maxInputType"
      :value="resolveInputVals[1]"
      @input="
        $event => {
          savedInputVals.max = $event
          wrapDebounce(() => {
            setInputVal($event, 'maxRange')
          })
        }
      "
      @keyup.enter="
        ($event: any) => {
          wrapDebounce.cancel()
          setInputVal($event.target.value, 'maxRange')
        }
      "
      @input-focus="onInputFocus('max')"
      @input-blur="
        () => {
          wrapDebounce.cancel()
          setInputVal(savedInputVals.max, 'maxRange')
          onInputBlur('max')
        }
      "
    />
  </div>

  <Slider
    class="range-slider"
    :min="minRange"
    :max="maxRange"
    :step="stepVal"
    :tooltips="false"
    v-model="sliderValues.array"
    @change="emit('valuesSelected', sliderValues.array)"
    @slide="setMinMaxVals"
  />
</template>

<script lang="ts" setup>
import Slider from '@vueform/slider'
import '@vueform/slider/themes/default.css'
import UiInput from '@sephora-asia/ui-input/UiInput.vue'
import debounce from 'lodash/debounce'
import { ref, reactive, computed, watch, nextTick } from 'vue'
import type { LocaleCodes } from './types'
import {
  SUFFIXED_CURRENCY_LOCALES,
  CURRENCY_SYMBOLS,
  FONT_SIZE_CHART,
} from './types'

interface Props {
  hasInputs: boolean
  minInputPlaceholder?: string
  maxInputPlaceholder?: string
  minRange: number // To not deal with decimals: Round the number in parent component
  maxRange: number
  selectedValue: {
    min: string
    max: string
  }
  locale?: LocaleCodes
  toResetVals?: boolean
  debounceMs?: number
}
interface Emits {
  (e: 'valuesSelected', values: Array<number>): void
  (e: 'rangeResetted'): void
}

// Vue-form-slider requires array of values for multiple sliders
interface ValueRange {
  array: Array<number>
}

const props = withDefaults(defineProps<Props>(), {
  // Defaults for: when API has not return price ranges
  hasInputs: true,
  minInputPlaceholder: 'Min Price',
  maxInputPlaceholder: 'Max Price',
  minRange: 0,
  maxRange: 100,
  locale: undefined,
  toResetVals: false,
  debounceMs: 500,
})
const emit = defineEmits<Emits>()

/**
 * ----- Data -----
 */

// Separate slider/input values because if there is too much logic to compute sliderValues, slider will feel laggy
// Better to update sliderValues independently
const sliderValues = reactive<ValueRange>({
  array: [props.minRange, props.maxRange],
})

const inputValues = reactive<ValueRange>({
  array: [props.minRange, props.maxRange],
})

const minInputType = ref<'text' | 'number'>('text')
const maxInputType = ref<'text' | 'number'>('text')

const isMinInputFocused = ref<boolean>(false)
const isMaxInputFocused = ref<boolean>(false)

const savedInputVals: { [key: string]: string | number } = {
  min: props.selectedValue.min,
  max: props.selectedValue.max,
}

/**
 * ----- Computed -----
 */

const resolveInputVals = computed((): Array<number | string> => {
  if (!props.locale) return inputValues.array

  // Sanitize for Intl.NumberFormat()
  const locale = props.locale.replace('_', '-')

  let formattedVals: Array<string>
  try {
    formattedVals = _formatInputVals(locale)
  } catch (error) {
    // Intl.NumberFormat() throws error when invalid locale code passed
    return inputValues.array
  }

  // Return min/max input as number if input in focus, otherwise formatted string
  if (isMinInputFocused.value) {
    return [inputValues.array[0], formattedVals[1]]
  } else if (isMaxInputFocused.value) {
    return [formattedVals[0], inputValues.array[1]]
  } else {
    return formattedVals
  }
})

const stepVal = computed((): number => {
  const maxRange = props.maxRange

  switch (true) {
    case maxRange >= 100000:
      return 5000
    case maxRange >= 10000:
      return 1000
    case maxRange >= 2000:
      return 100
    case maxRange >= 200:
      return 10
    case maxRange >= 100:
      return 5
    default:
      return 1
  }
})

const valFontSizes = computed(() => {
  let min, max

  const strVals = resolveInputVals.value.map(val => val.toString())

  // Characters like ., uses less space
  const toDeduct = strVals.map(strVal => {
    return (strVal.match(/\.|,/g)?.length || 0) * 0.5
  })

  if (strVals[0].length - toDeduct[0] >= FONT_SIZE_CHART.s.sizingLength) {
    min = FONT_SIZE_CHART.s.token
  }
  if (strVals[1].length - toDeduct[1] >= FONT_SIZE_CHART.s.sizingLength) {
    max = FONT_SIZE_CHART.s.token
  }

  return { min, max }
})

const inputBorderColor = computed(() => {
  let min, max

  if (isMinInputFocused.value) {
    min = 'var(--color-base-black)'
  } else min = 'var(--color-base-grey-30)'
  if (isMaxInputFocused.value) {
    max = 'var(--color-base-black)'
  } else max = 'var(--color-base-grey-30)'

  return { min, max }
})

/**
 * ----- Watchers -----
 */

// NOTE: Logic to reset vals when prop.(min/max)Range updated(not re-created) should be in parent component.
watch(
  () => props.toResetVals,
  (toReset: boolean) => {
    if (toReset) {
      setMinMaxVals([props.minRange, props.maxRange])
      emit('rangeResetted')
    }
  }
)
// Updates input value with parent's state if exist
watch(
  () => props.selectedValue,
  () => {
    if (sliderValues.array[0] !== Number(props.selectedValue.min)) {
      const customMin = props.selectedValue.min || props.minRange
      setInputVal(customMin, 'minRange', false)
    }
    if (sliderValues.array[1] !== Number(props.selectedValue.max)) {
      const customMax = props.selectedValue.max || props.maxRange
      setInputVal(customMax, 'maxRange', false)
    }
  },
  { immediate: true }
)

/**
 * ----- Methods -----
 */

async function setInputVal(
  val: string | number,
  range: 'minRange' | 'maxRange',
  shouldEmit = true // optional shouldEmit to prevent trigger of parent's update handler, if required
): Promise<void> {
  if (val === '') return // Allows input to be blank if backspaced all the way

  let numberVal: number = Number(val)

  const checkVal = {
    isFalsy: !numberVal, // Checks for: NaN, null, undefined, etc.
    isOverMinRange: numberVal < props.minRange,
    isOverMaxRange: numberVal > props.maxRange,
    isMinVal: range === 'minRange',
    isMaxVal: range === 'maxRange',
  }

  const checkValRange = {
    // Checks if minVal is higher than maxVal, vice versa. (returned as function to check updated numberVal)
    isMinOverMax: () => checkVal.isMinVal && numberVal > inputValues.array[1],
    isMaxUnderMin: () => checkVal.isMaxVal && numberVal < inputValues.array[0],
  }

  const rangeIndex = checkVal.isMinVal ? 0 : 1

  // To trigger reactivity, there must be value difference (new val cannot be same as old val),
  // This is needed to trigger update on ui-input with the correct input value.
  if (checkVal.isFalsy) {
    // Sets inputVal to a different value from final numberVal
    inputValues.array[rangeIndex] = props.maxRange + 1
    numberVal = props[range]
  } else if (checkVal.isOverMinRange || checkVal.isOverMaxRange) {
    inputValues.array[rangeIndex] = numberVal
    numberVal = props[range]
  }

  if (checkValRange.isMinOverMax()) {
    inputValues.array[rangeIndex] = numberVal
    numberVal = inputValues.array[1]
  } else if (checkValRange.isMaxUnderMin()) {
    inputValues.array[rangeIndex] = numberVal
    numberVal = inputValues.array[0]
  }

  // nextTick() needed to register changes to inputValues.array, before resetting it with the correct input values
  await nextTick()

  sliderValues.array[rangeIndex] = numberVal
  inputValues.array[rangeIndex] = numberVal
  shouldEmit && emit('valuesSelected', inputValues.array)
}

function setMinMaxVals(vals: Array<number>) {
  sliderValues.array = vals
  inputValues.array = vals
}

// TODO: move/refactor this logic into common repo/module when ready
function _formatInputVals(locale: string): Array<string> {
  const isSuffixed = SUFFIXED_CURRENCY_LOCALES.includes(locale)

  const currencySymbol =
    CURRENCY_SYMBOLS[locale.split('-')[1] as keyof typeof CURRENCY_SYMBOLS] ||
    ''

  return inputValues.array.map(val => {
    const formatter = _createFormatter(val, locale)
    const formattedVal = formatter.format(val)

    if (isSuffixed) {
      return `${formattedVal}${currencySymbol}`
    } else {
      return `${currencySymbol}${formattedVal}`
    }
  })
}

// This deals with floats
function _createFormatter(val: number, locale: string) {
  if (val % 1 !== 0) {
    return new Intl.NumberFormat(locale, { minimumFractionDigits: 2 })
  }
  return new Intl.NumberFormat(locale)
}

async function onInputFocus(range: 'min' | 'max'): Promise<void> {
  if (range === 'min') {
    // Triggers reactivity chain to resolve input value into NUMBER
    isMinInputFocused.value = true
    // Ensures resolved input value has been returned as number and not string
    await nextTick()
    // Changes input type to 'number' which accepts only number
    minInputType.value = 'number'
  }
  if (range === 'max') {
    isMaxInputFocused.value = true
    await nextTick()
    maxInputType.value = 'number'
  }
}

async function onInputBlur(range: 'min' | 'max'): Promise<void> {
  if (range === 'min') {
    // Resets input type to 'text' which accepts both string and number
    minInputType.value = 'text'
    // Ensures input type is now 'text'
    await nextTick()
    // Triggers reactivity chain to resolve input value into STRING
    isMinInputFocused.value = false
  }
  if (range === 'max') {
    maxInputType.value = 'text'
    await nextTick()
    isMaxInputFocused.value = false
  }
}

const wrapDebounce = debounce((callBack: () => any): void => {
  callBack()
}, props.debounceMs)
</script>

<style lang="scss" scoped>
.price-inputs-container {
  display: flex;
  justify-content: space-between;
  padding-bottom: 16px;
  gap: 8px;

  .price-input {
    max-width: 90px;
    min-width: 75px;
  }
}

:deep() {
  .slider-base {
    --slider-handle-width: 24px;
    --slider-handle-height: 24px;
    --slider-handle-bg: var(--color-base-white);
    --slider-handle-border: 1px solid var(--color-brand-primary-base);
    --slider-handle-shadow: none;

    --slider-bg: var(--color-brand-primary-lighter);
    --slider-connect-bg: var(--color-brand-primary-base);

    width: calc(100% - var(--slider-handle-width));
    margin: auto;
  }

  .slider-handle {
    transition: background-color 150ms ease-out;
    -webkit-transition: background-color 150ms ease-out;
    -moz-transition: background-color 150ms ease-out;
    -o-transition: background-color 150ms ease-out;
  }
  .slider-active {
    --slider-handle-bg: var(--color-brand-primary-base);
    --slider-handle-shadow-active: none;

    transition: background-color 200ms ease-out;
    -webkit-transition: background-color 200ms ease-out;
    -moz-transition: background-color 200ms ease-out;
    -o-transition: background-color 200ms ease-out;
  }

  #min-price-input {
    font-size: v-bind('valFontSizes.min');
    border-color: v-bind('inputBorderColor.min');
  }
  #max-price-input {
    font-size: v-bind('valFontSizes.max');
    border-color: v-bind('inputBorderColor.max');
  }

  #min-price-input,
  #max-price-input {
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    &[type='number'] {
      -moz-appearance: textfield;
    }
  }
}
</style>
